import type { Hall } from '../api/types';
import type { Key } from '../i18n';
import type { PhotoKey } from './imagery';

/**
 * The kinds of event a guest can name, and which venues suit them.
 *
 * Venues do not store an event-type list, so "suits" is read from what they do
 * store — the venue category — and said as a suggestion ("Good for"), never as
 * a promise. The values are the ones the booking request accepts: keep this
 * list in step with BOOKING_EVENT_TYPES in server.js.
 */
export const EVENT_TYPES = [
  'wedding', 'engagement', 'reception', 'birthday', 'conference', 'seminar',
  'workshop', 'cultural', 'music', 'corporate', 'launch', 'religious', 'other',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** The label for each event type, in the page's language. */
export const EVENT_TYPE_KEY: Record<EventType, Key> = {
  wedding: 'evWedding',
  engagement: 'evEngagement',
  reception: 'evReception',
  birthday: 'evBirthday',
  conference: 'evConference',
  seminar: 'evSeminar',
  workshop: 'evWorkshop',
  cultural: 'evCultural',
  music: 'evMusic',
  corporate: 'evCorporate',
  launch: 'evLaunch',
  religious: 'evReligious',
  other: 'evOther',
};

export const EVENT_TYPE_ICON: Record<EventType, string> = {
  wedding: 'ri-hearts-line',
  engagement: 'ri-vip-diamond-line',
  reception: 'ri-goblet-line',
  birthday: 'ri-cake-2-line',
  conference: 'ri-presentation-line',
  seminar: 'ri-slideshow-3-line',
  workshop: 'ri-tools-line',
  cultural: 'ri-music-2-line',
  music: 'ri-mic-line',
  corporate: 'ri-briefcase-4-line',
  launch: 'ri-rocket-2-line',
  religious: 'ri-candle-line',
  other: 'ri-star-smile-line',
};

export const EVENT_TYPE_PHOTO: Record<EventType, PhotoKey> = {
  wedding: 'weddingAisle',
  engagement: 'engagementRings',
  reception: 'receptionTable',
  birthday: 'balloons',
  conference: 'conferenceStage',
  seminar: 'seminarSpeaker',
  workshop: 'workshopRoom',
  cultural: 'theatreTiers',
  music: 'concertStage',
  corporate: 'corporateMeeting',
  launch: 'launchCrowd',
  religious: 'banquetRounds',
  other: 'stringLights',
};

/** Venue category → the events it is usually hired for. */
const SUITS_BY_CATEGORY: Record<string, EventType[]> = {
  'Wedding Hall': ['wedding', 'engagement', 'reception', 'birthday', 'religious', 'cultural'],
  'Banquet Hall': ['wedding', 'engagement', 'reception', 'birthday', 'corporate', 'launch'],
  'Convention Centre': ['conference', 'seminar', 'workshop', 'corporate', 'launch', 'wedding', 'reception', 'music'],
  Auditorium: ['conference', 'seminar', 'cultural', 'music', 'launch', 'religious', 'workshop'],
};

/** The events a venue is a good fit for, best first. */
export function eventsFor(hall: Pick<Hall, 'category'>): EventType[] {
  return SUITS_BY_CATEGORY[hall.category] ?? ['conference', 'cultural', 'wedding', 'birthday'];
}

/**
 * Whether a venue suits an event. An event type an admin added (lib/master.ts)
 * has no "good for" table here, so every venue is taken to suit it: filtering
 * by a new type must not empty the results.
 */
export const suits = (hall: Pick<Hall, 'category'>, ev: string) =>
  !(EVENT_TYPES as readonly string[]).includes(ev) || eventsFor(hall).includes(ev as EventType);

/**
 * The six occasion groups the public pages lead with. Each is a slug for the
 * URL (/occasions/:slug and /venues?occasion=slug), the event types it covers,
 * a photograph, and its copy keys.
 */
export interface Occasion {
  slug: string;
  events: EventType[];
  photo: PhotoKey;
  icon: string;
  title: Key;
  body: Key;
}

export const OCCASIONS: Occasion[] = [
  { slug: 'weddings', events: ['wedding', 'engagement', 'reception'], photo: 'weddingAisle', icon: 'ri-hearts-line', title: 'occWeddings', body: 'occWeddingsBody' },
  { slug: 'conferences', events: ['conference'], photo: 'conferenceStage', icon: 'ri-presentation-line', title: 'occConferences', body: 'occConferencesBody' },
  { slug: 'seminars', events: ['seminar', 'workshop'], photo: 'seminarSpeaker', icon: 'ri-slideshow-3-line', title: 'occSeminars', body: 'occSeminarsBody' },
  { slug: 'cultural', events: ['cultural', 'music', 'religious'], photo: 'theatreTiers', icon: 'ri-music-2-line', title: 'occCultural', body: 'occCulturalBody' },
  { slug: 'celebrations', events: ['birthday', 'reception', 'engagement'], photo: 'stringLights', icon: 'ri-cake-2-line', title: 'occCelebrations', body: 'occCelebrationsBody' },
  { slug: 'corporate', events: ['corporate', 'launch', 'conference'], photo: 'corporateMeeting', icon: 'ri-briefcase-4-line', title: 'occCorporate', body: 'occCorporateBody' },
];

export const occasionBySlug = (slug: string | null | undefined) => OCCASIONS.find((o) => o.slug === slug);

/** Whether a venue suits any of an occasion's events. */
export const fitsOccasion = (hall: Pick<Hall, 'category'>, occ: Occasion) => occ.events.some((e) => suits(hall, e));
